import { type AxiosError } from 'axios'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import store from 'store'

import authentication from 'api/authentication'
import { signInUsingSaml } from 'components/authentication/authentication-utils'
import { CompanyListForm } from 'components/authentication/CompanyListForm'
import { createGoogleNonce } from 'components/authentication/google-oauth'
import { MFAForm } from 'components/authentication/MFAForm'
import { signInReducer } from 'components/authentication/reducers/signin-reducer'
import { SignInForm } from 'components/authentication/SignInForm'
import Modal, { ModalHeader, ModalCloseButton } from 'components/Modal'
import { MFATokenEntry } from 'components/profile/MFATokenEntry'
import {
  isChooseCompanyAuthResponse,
  isMFAAuthResponse
} from 'context/auth-context-utils'
import useAppRefresh from 'hooks/use-app-refresh'
import useAuth from 'hooks/use-auth'
import { appConfig, isPrivateEdition } from 'utils/app-config'
import {
  messageFromResponseError,
  getFilteredRedirectPath
} from 'utils/strings'

import type { MFAAuthResponse, UserAuthCompany } from 'types/api/user/session'

export enum SignInActions {
  RESET = 'reset',
  ENTER_TOKEN = 'set_token',
  SELECT_COMPANY = 'set_company',
  VALIDATE_CREDENTIALS = 'validate_credentials',
  UPDATE_EMAIL = 'update_email',
  UPDATE_PASSWORD = 'update_password',
  UPDATE_TOKEN = 'update_token',
  UPDATE_COMPANY_NAME = 'update_company_name'
}

export enum AuthMode {
  CREDENTIALS = 'credentials',
  TOKEN = 'token',
  COMPANIES = 'companies'
}
export interface SignInCredentials {
  mode: AuthMode
  email: string
  password: string
  token: string
  companyName: string
  companyList: UserAuthCompany[]
  mfaResponse: MFAAuthResponse | null
}

export const initialSignInState: SignInCredentials = {
  mode: AuthMode.CREDENTIALS,
  email: '',
  password: '',
  token: '',
  companyName: '',
  companyList: [],
  mfaResponse: null
}

export const SignIn: React.FC<{ initialCredentials?: SignInCredentials }> = ({
  initialCredentials = initialSignInState
}) => {
  const navigate = useNavigate()
  const authState = useAuth()
  const appRefresh = useAppRefresh()
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [signInError, setSignInError] = React.useState<string>()
  const [signInCredentials, signInCredentialsDispatcher] = React.useReducer(
    signInReducer,
    initialCredentials
  )

  const handlePasswordLogin = async (
    selectedCompany = signInCredentials.companyName
  ) => {
    signInCredentialsDispatcher({
      type: SignInActions.UPDATE_COMPANY_NAME,
      companyName: selectedCompany
    })
    setSignInError(undefined)
    setIsSubmitting(true)

    try {
      const authResponse = await authentication.signin({
        email: signInCredentials.email,
        password: signInCredentials.password,
        token: signInCredentials.token,
        companyName: selectedCompany
      })

      if (authResponse === 'useMfaAuth') {
        signInCredentialsDispatcher({ type: SignInActions.ENTER_TOKEN })
        setIsSubmitting(false)
        return
      }

      if (isChooseCompanyAuthResponse(authResponse)) {
        signInCredentialsDispatcher({
          type: SignInActions.SELECT_COMPANY,
          companyList: authResponse.companies
        })
        setIsSubmitting(false)
        return
      }

      if (isMFAAuthResponse(authResponse)) {
        // Need to validate your mfaToken
        signInCredentialsDispatcher({
          type: SignInActions.VALIDATE_CREDENTIALS,
          mfaResponse: authResponse
        })
        setIsSubmitting(false)
        return
      }

      await authState.startSession(authResponse)
      // Refresh Webapp When Logging In
      // since we're setting the href explicitly below, that reloads the webapp
      // so if it's marked as needing to be refreshed, then we can clear that here
      appRefresh.markAsRefreshed()

      const redirectUrl = store.get('loginRedirect')
      store.remove('loginRedirect')
      navigate(getFilteredRedirectPath(redirectUrl))
    } catch (err) {
      handleError(err, 'Invalid username or password.')
      setIsSubmitting(false)
    }
  }

  const handleGoogleLogin = async () => {
    try {
      // same as above with password login
      // we're refreshing the webapp since we reload the page here
      appRefresh.markAsRefreshed()

      window.location.href = authentication.generateGoogleUrl({
        nextPathname: store.get('loginRedirect') ?? '/',
        nonce: createGoogleNonce()
      })
    } catch (err) {
      handleError(
        err,
        'There was an issue communicating with Google. Please try again later.'
      )
    }
  }

  const { companyName } = appConfig.getState()

  const handleSamlLogin = async () => {
    if (isPrivateEdition() && companyName) {
      signInUsingSaml(appRefresh, companyName)
    } else {
      navigate('/login/saml')
    }
  }

  const handleError = (err: AxiosError | null, defaultText?: string) => {
    const errorText = messageFromResponseError(err, defaultText)
    setSignInError(errorText)
  }

  if (signInCredentials.mode === AuthMode.TOKEN) {
    return (
      <MFAForm
        handleBack={() =>
          signInCredentialsDispatcher({ type: SignInActions.RESET })
        }
        handleTokenChange={(token) =>
          signInCredentialsDispatcher({
            type: SignInActions.UPDATE_TOKEN,
            token
          })
        }
        handleTokenSubmission={handlePasswordLogin}
        signInError={signInError}
        submitting={isSubmitting}
        token={signInCredentials.token}
      />
    )
  }

  if (signInCredentials.mode === AuthMode.COMPANIES) {
    return (
      <CompanyListForm
        companies={signInCredentials.companyList}
        handleBack={() =>
          signInCredentialsDispatcher({ type: SignInActions.RESET })
        }
        handleCompanySelection={(companyName) =>
          handlePasswordLogin(companyName)
        }
        signInError={signInError}
        submitting={isSubmitting}
      />
    )
  }

  if (signInCredentials.mfaResponse) {
    return (
      <Modal isOpen={Boolean(signInCredentials.mfaResponse)}>
        <ModalHeader>
          <ModalCloseButton onClick={() => window.location.reload()} />
        </ModalHeader>
        <MFATokenEntry
          isSignIn
          email={signInCredentials.email}
          mfaResponse={signInCredentials.mfaResponse}
          validateMFASuccess={handlePasswordLogin}
          onCancel={() => window.location.reload()}
        />
      </Modal>
    )
  }

  return (
    <SignInForm
      email={signInCredentials.email}
      handleEmailChange={(email) =>
        signInCredentialsDispatcher({
          type: SignInActions.UPDATE_EMAIL,
          email
        })
      }
      handleGoogleLogin={handleGoogleLogin}
      handlePasswordChange={(password) =>
        signInCredentialsDispatcher({
          type: SignInActions.UPDATE_PASSWORD,
          password
        })
      }
      handlePasswordLogin={handlePasswordLogin}
      handleSamlLogin={handleSamlLogin}
      password={signInCredentials.password}
      signInError={signInError}
      submitting={isSubmitting}
    />
  )
}
